
import { google } from 'google-maps';
import { Component, Mixins, Watch } from 'vue-property-decorator';
import { GoogleMapConfig } from '@/flightpath-components/types/GoogleMapConfig';
import GoogleMapLoader from '@/components/Map/GoogleMapLoader.vue';
import GoogleMapMachineMarker from '@/components/Map/GoogleMapMachineMarker.vue';
import GoogleMapPolyLine from '@/components/Map/GoogleMapPolyLine.vue';
import GoogleMapPolygon from '@/components/Map/GoogleMapPolygon.vue';
import GoogleMapGroundOverlay from '@/components/Map/GoogleMapGroundOverlay.vue';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import { PubSub } from '@/PubSubClass';
import { awsConfiguration } from '@/aws-configuration';
import { MqttMessage } from '@/flightpath-components/models/mqttPayload/GPSUpdateMessage';
import { MachineMarker } from '@/flightpath-components/models/mqttPayload/MachineMarkerMessage';
import { MachineMarkerOptions } from '@/flightpath-components/models/MachineMarkerOptions';
import { SinglePathMission } from '@/flightpath-components/models/SinglePathMission';
import { Overlay } from '@/flightpath-components/models/Overlay';
import { MachineKey } from '@/flightpath-components/models/MachineKey';
import { Container } from 'inversify';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import MachineStore from '@/store/modules/machine';
import MissionStore from '@/store/modules/missions';
import { getModule } from 'vuex-module-decorators';
import { GridLocation } from '@/flightpath-components/models/GridLocation';
import MqttMixIn from '@/mixins/mqttManagement';
import MenuStore from '@/store/modules/menu';
import { RenderHelper } from '@/services/RenderHelper';
import GlobalStore from '@/store/modules/global';
import UpdateStore from '@/store/modules/update';
import { UpdateMachineUpdateIcon } from '@/models/Icons';
import moment from 'moment';

@Component({
  inject: ['container'],
  // Specify `components` option.
  // See Vue.js docs for all available options:
  // https://vuejs.org/v2/api/#Options-Data
  components: {
    GoogleMapLoader,
    GoogleMapMachineMarker,
    GoogleMapPolyLine,
    GoogleMapPolygon,
    GoogleMapGroundOverlay
  }
})
export default class BaseMap extends Mixins(MqttMixIn) {
  apiKey = awsConfiguration.apiKey;

  markers: Array<MachineMarkerOptions> = [];

  polyLines: Array<google.maps.PolylineOptions> = [];

  polygons: Array<google.maps.PolygonOptions> = [];

  overlays: Array<Overlay> = [];

  machineKey: MachineKey = { machineId: 'ded65af1-0ae1-4bb0-8cb6-5a40d4872f19' };

  missionPlan: SinglePathMission | null = null;

  interval: number | undefined = undefined;

  machineStore = getModule(MachineStore, this.$store);

  missionStore = getModule(MissionStore, this.$store);

  menuStore = getModule(MenuStore, this.$store);

  globalStore = getModule(GlobalStore, this.$store);

  updateStore = getModule(UpdateStore, this.$store);

  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  private container!: Container;

  _processing = false;

  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
    this.addMqttVisibility(this.globalStore.machineMarkerTopic, () =>
      PubSub.subscribe(this.globalStore.machineMarkerTopic).subscribe({
        next: (data) => this.markerRecieved(data),
        error: (error) => this.mqttError(error)
      })
    );
  }

  async mounted() {
    if (this.flightPathService != undefined) await this.flightPathService.updateToken();
    this.actions.forEach((act, key) => {
      this.subscriptions.set(key, act());
    });
    this.getMissionPlan();
  }

  destroyed() {
    clearInterval(this.interval);
    this.removeMqttVisibility();
  }

  @Watch('missionStore.mission')
  onMissionChange() {
    this.missionPlan = this.missionStore.mission;
    this.renderMissionPlan();
  }

  getGridLocation(url: string): GridLocation | undefined {
    const regex = /([0-9-])+/g;

    const matches = url.match(regex);
    if (matches != null && matches.length >= 2) {
      const xVal = matches[0];
      const yVal = matches[1];

      return { X: Number.parseInt(xVal), Y: Number.parseInt(yVal) };
    }
  }

  /**
   * Get current active mission plan and render field definition
   */
  private async getMissionPlan() {
    if (this.flightPathService == undefined) return;
    this.menuStore.setLoadingMachines(true);

    this.missionPlan = this.missionStore.mission;
    this.renderMissionPlan();

    try {
      const machineResponse = await this.flightPathService.getAvailableMachines();
      if (machineResponse != null) {
        const json = JSON.stringify(machineResponse.data);
        const availableMachines = JSON.parse(json) as Array<MachineKey>;
        this.machineStore.setAvailableMachines(availableMachines);
        if (this.flightPathService == undefined) return;
        const machineStatusResponse = await this.flightPathService.getMachineStatus(
          availableMachines.map((map) => {
            return map.machineId;
          })
        );
        if (machineStatusResponse != null) {
          const json = JSON.stringify(machineStatusResponse.data);

          const cachedmachines = JSON.parse(json) as Array<MachineMarker>;

          cachedmachines.forEach((element) => {
            this.updatePosition(element);
          });
          const machines = cachedmachines
            .filter((val) => new Date(val.SentUTC).getTime() > moment().subtract(2, 'months').toDate().getTime())
            .map((val) => val.Key);
          this.machineStore.setAvailableMachines(machines);
        }
      }
    } finally {
      this.menuStore.setLoadingMachines(false);
    }
  }

  private renderMissionPlan() {
    this.polygons = [];
    this.polyLines = [];

    if (this.missionPlan == null) return;

    this.polygons.push(RenderHelper.getFieldPolygonOptionsFromMission(this.missionPlan));
    this.polygons.push(RenderHelper.getWorkAreaPolygonOptionsFromMission(this.missionPlan));
    this.polyLines.push(RenderHelper.getPathPolylineOptionsFromMission(this.missionPlan));
  }

  private markerRecieved(data: MqttMessage): void {
    this.updatePosition(data.value);
  }

  private updatePosition(data: MachineMarker) {
    if (data.Location == null) return;
    const selectedMachine = this.machineStore.selectedMachine;
    const machineUpdateIndex = this.updateStore.machineUpdates.findIndex(
      (o) => o.machine_id.localeCompare(data.Key.machineId, undefined, { sensitivity: 'base' }) == 0
    );
    if (machineUpdateIndex >= 0) {
      const statusUpdate = this.updateStore.machineUpdates[machineUpdateIndex];
      data = UpdateMachineUpdateIcon(data, statusUpdate);
    }
    const marker: MachineMarkerOptions = {
      UpdateMessage: data,
      Machine: data.Key,
      MarkerOptions: {
        position: {
          lat: data.Location.Location.Latitude,
          lng: data.Location.Location.Longitude
        },
        zIndex: selectedMachine != null && selectedMachine.machineId == data.Key.machineId ? 99 : 1,
        title: `Machine: ${data.Key.MachineName ?? data.Key.machineId}`
      }
    };
    const machineKey = (data as MachineMarker).Key;

    const index = this.markers.findIndex((val) => val.Machine.machineId == machineKey.machineId);

    if (index >= 0) {
      this.markers.splice(index, 1, marker);
    } else {
      this.markers.push(marker);
    }
  }

  get mapConfig(): GoogleMapConfig {
    return {
      locationControl: false,
      loaderOptions: { libraries: ['geometry'] },
      mapOptions: {
        styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
        zoom: 17,
        center: this.globalStore.defaultMapLocation,
        mapTypeId: 'hybrid',
        mapTypeControl: false,
        zoomControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        clickableIcons: false,
        disableDefaultUI: true
      }
    };
  }
}
